import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '../../lib/utils'

/**
 * StatusBadge — grammaire d'etats unifiee
 *
 * Primitive INTENT-based : elle ne connait pas les statuts metier
 * (requested, confirmed, in_progress, etc.). Le mapping
 * statut metier -> intent est la responsabilite de l'app
 * (via un helper du type getStatusIntent(evac.status)).
 *
 * Intents disponibles :
 *   - success  : evacuation validee, flux actif, conformite OK
 *   - warning  : en attente d'action, alerte non bloquante
 *   - error    : erreur bloquante, refus, action impossible
 *   - info     : information neutre, hint utilisateur
 *   - neutral  : etat neutre, pas de charge semantique
 *
 * Tailles :
 *   - sm (defaut) : 20px, text-label, usage inline dans les tables
 *   - md          : 24px, text-caption, usage standalone/cards
 *
 * Usage :
 *   <StatusBadge intent="success">Termine</StatusBadge>
 *   <StatusBadge intent="warning" size="md">En attente</StatusBadge>
 */
const statusBadgeVariants = cva(
  'inline-flex items-center gap-1.5 rounded-md font-semibold uppercase tracking-wider whitespace-nowrap',
  {
    variants: {
      intent: {
        success: 'bg-success-bg text-success border border-success/20',
        warning: 'bg-warning-bg text-warning border border-warning/20',
        error:   'bg-error-bg   text-error   border border-error/20',
        info:    'bg-info-bg    text-info    border border-info/20',
        neutral: 'bg-surface-low text-ink-secondary border border-ink-ghost/30',
      },
      size: {
        sm: 'h-5 px-2 text-label',
        md: 'h-6 px-2.5 text-caption',
      },
      dot: {
        true:  '',
        false: '',
      },
    },
    defaultVariants: {
      intent: 'neutral',
      size: 'sm',
      dot: false,
    },
  }
)

const dotVariants = cva('w-1.5 h-1.5 rounded-full shrink-0', {
  variants: {
    intent: {
      success: 'bg-success',
      warning: 'bg-warning',
      error:   'bg-error',
      info:    'bg-info',
      neutral: 'bg-ink-muted',
    },
  },
  defaultVariants: {
    intent: 'neutral',
  },
})

export interface StatusBadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof statusBadgeVariants> {
  /** Affiche un pastille colore a gauche du label. Utile dans les tables denses. */
  dot?: boolean
}

const StatusBadge = React.forwardRef<HTMLSpanElement, StatusBadgeProps>(
  ({ className, intent, size, dot, children, ...props }, ref) => {
    return (
      <span
        ref={ref}
        className={cn(statusBadgeVariants({ intent, size }), className)}
        {...props}
      >
        {dot ? <span className={dotVariants({ intent })} aria-hidden /> : null}
        {children}
      </span>
    )
  }
)
StatusBadge.displayName = 'StatusBadge'

export { StatusBadge, statusBadgeVariants }
